import type { Connection, Matter, MatterContactConnection } from "@/lib/types";
import {
  getMatterPartyConnections,
  toMatterContactConnectionPresentation,
} from "@/lib/utils/connections/matter-party-connections";

type MatterContactConnectionRecord = Record<string, unknown>;

const asConnectionRecord = (
  connection:
    | Partial<Connection>
    | Partial<MatterContactConnection>
    | MatterContactConnectionRecord,
): MatterContactConnectionRecord => connection as MatterContactConnectionRecord;

export const getlabel = (
  connection:
    | Partial<Connection>
    | Partial<MatterContactConnection>
    | MatterContactConnectionRecord,
): string => {
  const record = asConnectionRecord(connection);
  const label = record.label;
  if (typeof label === "string") return label;
  const connectionType = record.connectionType;
  if (typeof connectionType !== "string") return "";
  return (connectionType.split("_to_").at(-1) ?? "")
    .replace(/^legacy_/, "")
    .split("_")
    .map((word) =>
      word === "medpay"
        ? "MedPay"
        : `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    )
    .join(" ");
};

export const getdescription = (
  connection: Partial<MatterContactConnection> | MatterContactConnectionRecord,
): string | undefined =>
  (asConnectionRecord(connection).description ??
    asConnectionRecord(connection).description) as string | undefined;

export const getMatterContactConnectionLinkedId = (
  connection: Partial<MatterContactConnection> | MatterContactConnectionRecord,
  key: keyof MatterContactConnection,
): string | undefined => {
  const raw = asConnectionRecord(connection);
  const v2Key = key.startsWith("matter")
    ? `${key.slice("matter".length, -"Id".length).charAt(0).toLowerCase()}${key.slice("matter".length + 1, -"Id".length)}Id`
    : key;
  return (raw[v2Key] ?? raw[key]) as string | undefined;
};

/** Matter-level party roles that use the client (green) hero. */
export const MATTER_CLIENT_PARTY_CONNECTION_LABELS = ["Client"] as const;

/** Matter-level party roles that use the defendant (blue) hero and Defendants UI. */
export const MATTER_DEFENDANT_PARTY_CONNECTION_LABELS = ["Defendant"] as const;

/** Related contacts shown on the Client tab (not standalone Other Parties). */
export const MATTER_CLIENT_SECTION_AUXILIARY_CONNECTION_LABELS = [
  "Primary Contact",
  "Spouse",
  "Emergency Contact",
  "Parent",
] as const;

/**
 * Junction label linking a statute of limitations to an existing defendant contact.
 * Not a separate party — do not list these in Defendants / Other Parties UI.
 */
export const MATTER_STATUTE_DEFENDANT_CONNECTION_LABEL = "Statute Defendant";

export function isStatuteDefendantConnection(
  connection: Pick<MatterContactConnection, "label" | "statuteOfLimitationsId">,
): boolean {
  return (
    getlabel(connection) === MATTER_STATUTE_DEFENDANT_CONNECTION_LABEL ||
    Boolean(
      getMatterContactConnectionLinkedId(connection, "statuteOfLimitationsId"),
    )
  );
}

function normalizeConnectionLabelForRole(label?: string | null): string {
  return (label ?? "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();
}

export function getMatterContactConnections(
  matter: Pick<Matter, "id"> & Partial<Pick<Matter, "connections">>,
): MatterContactConnection[] {
  return getMatterPartyConnections(matter).map((connection) =>
    toMatterContactConnectionPresentation(connection, matter.id),
  );
}

/** Case-insensitive match for matter connection labels (handles nbsp / spacing). */
export function labelsMatch(
  left?: string | null,
  right?: string | null,
): boolean {
  return (
    normalizeConnectionLabelForRole(left) ===
    normalizeConnectionLabelForRole(right)
  );
}

/** Whether this connection label is the matter client role (matches any known client label). */
export function isClientPartyConnectionLabel(label?: string | null): boolean {
  const normalized = normalizeConnectionLabelForRole(label);
  return MATTER_CLIENT_PARTY_CONNECTION_LABELS.some(
    (role) => role.toLowerCase() === normalized,
  );
}

/** Whether this connection label is a defendant party role (matches any known defendant label). */
export function isDefendantPartyConnectionLabel(
  label?: string | null,
): boolean {
  const normalized = normalizeConnectionLabelForRole(label);
  return MATTER_DEFENDANT_PARTY_CONNECTION_LABELS.some(
    (role) => role.toLowerCase() === normalized,
  );
}

const OTHER_PARTY_LABELS = ["Witness", "Expert", "Third Party", "Other"];

/**
 * Other Parties tab: connections with labels that belong in the other-party
 * section. Linked vehicle/insurance/etc. connections are intentionally ignored
 * only by label, not by whether the same contact appears elsewhere.
 */
export function isOtherPartyMatterContactConnection(
  _matter: Matter,
  connection: MatterContactConnection,
): boolean {
  return OTHER_PARTY_LABELS.includes(getlabel(connection));
}

/** Connections shown in the Other Parties tab (shared by the tab badge and section). */
export function getOtherPartyMatterContactConnections(
  matter: Matter,
): MatterContactConnection[] {
  return getMatterContactConnections(matter).filter((connection) =>
    isOtherPartyMatterContactConnection(matter, connection),
  );
}

/** Contacts whose coverage can be pursued as third-party coverage. */
export function getThirdPartyMatterContactIds(matter: Matter): Set<string> {
  return new Set(
    getMatterContactConnections(matter)
      .filter(
        (connection) =>
          isDefendantPartyConnectionLabel(getlabel(connection)) ||
          isOtherPartyMatterContactConnection(matter, connection),
      )
      .map((connection) => connection.contactId)
      .filter(Boolean),
  );
}

export type MatterContactLabelPair = {
  contactId: string;
  label: MatterContactConnection["label"];
};

/** Existing other-party contact + label pairs (for duplicate prevention in add UI). */
export function getOtherPartyContactLabelPairs(
  matter: Matter,
): MatterContactLabelPair[] {
  return getOtherPartyMatterContactConnections(matter).map((connection) => ({
    contactId: connection.contactId,
    label: getlabel(connection),
  }));
}

/**
 * True if this contact has at least one client-role connection on the matter.
 */
export function matterContactHasClientPartyRole(
  matter: Matter,
  matterContactConnectionContactId: string,
): boolean {
  return getMatterContactConnections(matter).some(
    (contact) =>
      contact.contactId === matterContactConnectionContactId &&
      isClientPartyConnectionLabel(getlabel(contact)),
  );
}

/**
 * True if this contact has at least one defendant-party connection on the matter.
 */
export function matterContactHasDefendantPartyRole(
  matter: Matter,
  matterContactConnectionContactId: string,
): boolean {
  return getMatterContactConnections(matter).some(
    (contact) =>
      contact.contactId === matterContactConnectionContactId &&
      isDefendantPartyConnectionLabel(getlabel(contact)),
  );
}

/**
 * Milliseconds for sorting: when this party was linked on the matter.
 *
 * Uses {@link MatterContactConnection}'s `dateCreated` from `commonMatterFields` (the **connection**
 * row’s audit timestamp in our schema). This is not read from nested contact profile fields or from
 * `retrieveContact` — only from the matter–contact connection payload.
 */
export function getMatterContactConnectionRecordCreatedAtMs(
  connection: MatterContactConnection,
): number {
  const ms = new Date(connection.dateCreated).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Defendants on the matter (and assign-defendant pickers): at-fault first, then by **matter contact
 * connection** record creation time ascending (newest link at the bottom). Tie-break: connection id.
 */
export function compareMatterDefendantConnectionsDisplayOrder(
  left: MatterContactConnection,
  right: MatterContactConnection,
): number {
  const leftIsAtFault =
    (asConnectionRecord(left).faultStatus ?? left.faultStatus) === "At Fault";
  const rightIsAtFault =
    (asConnectionRecord(right).faultStatus ?? right.faultStatus) === "At Fault";

  if (leftIsAtFault !== rightIsAtFault) {
    return leftIsAtFault ? -1 : 1;
  }

  const leftMs = getMatterContactConnectionRecordCreatedAtMs(left);
  const rightMs = getMatterContactConnectionRecordCreatedAtMs(right);
  if (leftMs !== rightMs) {
    return leftMs - rightMs;
  }
  return String(left.id).localeCompare(String(right.id));
}
